// @ts-nocheck
import React from 'react';
import { ITempProjectModel, ProjectScope } from '../models/TempProjectModel';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import IAutocompleteValue from '../models/IAutocompleteValue';

interface ISelectProjectProps {
  projects: ITempProjectModel[];
  onChange: Function;
  selectProjectId: number;
  currentProjectName?: string;
  renderContent?: boolean;
  isAdministrator?: boolean;
  projectScope?: ProjectScope;
  onProjectScopeChange?: (scope: ProjectScope) => void;
  isRefreshing?: boolean;
  showEndedProjects?: boolean;
  onShowEndedProjectsChange?: (show: boolean) => void;
  disablePortal?: boolean;
}

const defaultOption: IAutocompleteValue = { label: 'Choose your project', value: null };

const SelectProject: React.FC<ISelectProjectProps> = ({
  projects,
  onChange,
  selectProjectId,
  currentProjectName,
  renderContent,
  isAdministrator = false,
  projectScope = 'assigned',
  onProjectScopeChange,
  isRefreshing = false,
  showEndedProjects = false,
  onShowEndedProjectsChange,
  disablePortal = false,
}) => {
  const projectName = projects?.find((p) => p.id === selectProjectId)?.projectName;

  const projectOptions: IAutocompleteValue[] = projects.map((proj) => ({
    label: proj.projectName,
    value: proj.id,
    isEnded: proj.isEnded,
  }));

  const options: IAutocompleteValue[] = (() => {
    const result: IAutocompleteValue[] = [];

    // Case 1: If you opened project you are not assigned to and you are admin
    if (currentProjectName && !projectOptions.some((opt) => opt.label === currentProjectName)) {
      result.push({ label: currentProjectName, value: null });
    }

    // Case 2: If you opened project with error
    if (!projectName && !currentProjectName) {
      result.push(defaultOption);
    }

    result.push(...projectOptions);
    return result;
  })();

  const value =
    options.find((opt) => opt.value === selectProjectId) ??
    options.find((opt) => opt.label === currentProjectName) ??
    options.find((opt) => opt.value !== null) ??
    (options.length > 0 ? options[0] : defaultOption);

  return (
    <Autocomplete
      className="kanban-board__selector"
      size="small"
      value={value}
      disableClearable
      disablePortal={disablePortal}
      options={isRefreshing ? [] : options}
      loading={isRefreshing}
      disabled={renderContent}
      PaperComponent={({ children, ...paperProps }) => (
        <Paper {...paperProps}>
          {isAdministrator && (
            <Tabs
              value={projectScope}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, value) => {
                onProjectScopeChange?.(value);
              }}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="assigned" label="My projects" disabled={isRefreshing} />
              <Tab value="all" label="All projects" disabled={isRefreshing} />
            </Tabs>
          )}
          {isAdministrator && (
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                color: 'text.secondary',
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                cursor: 'pointer',
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <Checkbox
                size="small"
                checked={showEndedProjects}
                disabled={isRefreshing}
                onChange={(event) => onShowEndedProjectsChange?.(event.target.checked)}
              />
              Show ended projects
            </Box>
          )}
          {children}
        </Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          margin="none"
          sx={{ m: 0 }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {renderContent ? (
                  <div className="loading">
                    <CircularProgress size={20} className="loading-spinner" />
                    Loading...
                  </div>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      onChange={(e, value) => {
        if (value?.value !== null && value?.value !== undefined) {
          onChange(value.value);
        }
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionKey={(option) => String(option.value ?? `placeholder-${option.label}`)}
      getOptionDisabled={(option) => option.value === null}
      renderOption={(props, option) => {
        const { key: _optionKey, ...optionProps } = props;

        return (
          <li
            key={String(option.value ?? `placeholder-${option.label}`)}
            {...optionProps}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              ...optionProps.style,
            }}
            sx={{
              color: (theme) =>
                option.isEnded
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.55)'
                    : 'rgba(0, 0, 0, 0.45)'
                  : 'inherit',
            }}
          >
            <Box
              component="span"
              sx={{
                color: option.isEnded
                  ? (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.6)'
                        : 'rgba(0, 0, 0, 0.5)'
                  : 'inherit',
                textDecoration: option.isEnded ? 'line-through' : undefined,
              }}
            >
              {option.label}
            </Box>
            {option.isEnded && (
              <Chip
                label="Ended"
                size="small"
                sx={{
                  ml: 'auto',
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.error.light
                      : theme.palette.error.dark,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(244, 67, 54, 0.2)'
                      : alpha(theme.palette.error.main, 0.08),
                  border: 1,
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(244, 67, 54, 0.45)'
                      : 'rgba(198, 40, 40, 0.2)',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              />
            )}
          </li>
        );
      }}
    />
  );
};

export default SelectProject;
